// Single source of truth for each user's feature permissions, resolved from the
// user's identity (email / Parse username). HARDCODED here for now — the RBAC
// layer does not read the API `is_admin` array at all.
//
// A permission map is shaped { [Feature]: [actions] } and is read as a set of
// RESTRICTIONS (see defineAbilityFor in abilities.js): a user has full access to
// everything EXCEPT the features listed here, which are limited to the actions shown.
//   [VIEW]        -> view-only (cannot edit)
//   [VIEW, EDIT]  -> full access (listing it changes nothing; usually omit it)
//   []            -> feature fully denied (its route is hidden too)
// Only list the features you want to LIMIT. Returning null (unlisted user) => no
// restrictions => full access.
//
// SWITCHING TO THE BACKEND LATER: when the API returns the map on the user
// (e.g. state.auth.featurePermissions), this collapses to:
//   export const getUserAccess = (auth) => auth.featurePermissions || null;
// Nothing else (AbilityProvider, abilities.js, components) needs to change.

import { FEATURES, FEATURE_ACTIONS } from './features';

const { VIEW, EDIT } = FEATURE_ACTIONS;
const { SHIFT_REPORT, DISPATCH_BOARD, DRIVER_SHIFT, DASHBOARD, RESERVATIONS, BOOKINGS, SUBSCRIPTION } = FEATURES;

// Allow-list helper for users who may use ONLY a few areas. Because the map is read
// as RESTRICTIONS (omitted feature => full access), granting access to just a few
// features means DENYING every other one: this sets every FEATURE to [] (fully
// denied) and then applies the passed grants on top.
//   allowOnly({ [BOOKINGS]: [VIEW, EDIT] })  =>  only Bookings, everything else denied
const allowOnly = (grants) => {
  const denyAll = {};
  Object.values(FEATURES).forEach((feature) => {
    denyAll[feature] = [];
  });
  return { ...denyAll, ...grants };
};

// Add a line per user. Keyed by lowercased email/username.
const PERMISSIONS_BY_IDENTITY = {
  // Jing Zhu (objectId nW3Aetufsi) d
  // 'dashboardstory@gmail.com': {
  //   [SHIFT_REPORT]: [VIEW],
  //   [DISPATCH_BOARD]: [VIEW, EDIT],
  //   [DRIVER_SHIFT]: [VIEW, EDIT],
  // },
  // Jing Duet d'
  'ride@duetinc.com': {
    [SHIFT_REPORT]: [VIEW, EDIT],
    [DISPATCH_BOARD]: [VIEW],
    [DRIVER_SHIFT]: [VIEW],
  },
  // The Leap Institute — view-only staff (can see, cannot change).
  // Shift Report view-only; full access to every other area (Dispatch Board + Driver Shift):
  'enavarro@theleapinstitute.org': {
    [SHIFT_REPORT]: [VIEW],
    [DISPATCH_BOARD]: [VIEW, EDIT],
    [DRIVER_SHIFT]: [VIEW, EDIT],
  },

  'sjimenez@theleapinstitute.org': {
    [SHIFT_REPORT]: [VIEW],
    [DISPATCH_BOARD]: [VIEW, EDIT],
    [DRIVER_SHIFT]: [VIEW, EDIT],
  },
  // Driver Shift + Dispatch Board, both view-only:
  'blopez@theleapinstitute.org': {
    [SHIFT_REPORT]: [VIEW],
    [DISPATCH_BOARD]: [VIEW],
    [DRIVER_SHIFT]: [VIEW, EDIT],
  },
  'anunez@theleapinstitute.org': {
    [SHIFT_REPORT]: [VIEW],
    [DISPATCH_BOARD]: [VIEW, EDIT],
    [DRIVER_SHIFT]: [VIEW, EDIT],
  },
  'tjdecastro@theleapinstitute.org': {
    [SHIFT_REPORT]: [VIEW],
    [DISPATCH_BOARD]: [VIEW],
    [DRIVER_SHIFT]: [VIEW, EDIT],
  },
  'joey@theleapinstitute.org': {
    [SHIFT_REPORT]: [VIEW],
    [DISPATCH_BOARD]: [VIEW],
    [DRIVER_SHIFT]: [VIEW, EDIT],
  },
  'yeseniapimentel980@gmail.com': {
    [SHIFT_REPORT]: [VIEW],
    [DISPATCH_BOARD]: [VIEW],
    [DRIVER_SHIFT]: [VIEW, EDIT],
  },
  // Courtney Winbush — allow-list only. May use ONLY: Service Map (the /dashboard
  // page), Reservations, Bookings (current + future), and Subscription. Every other
  // area is denied. DASHBOARD covers the whole Dashboard menu group (Service Map plus
  // Task Panel / ride-detail sub-pages), since they share one feature.
  'courtney.winbush05@gmail.com': allowOnly({
    [DASHBOARD]: [VIEW, EDIT],
    [RESERVATIONS]: [VIEW, EDIT],
    [BOOKINGS]: [VIEW, EDIT],
    [SUBSCRIPTION]: [VIEW, EDIT],
  }),
  'dashboardstory@gmail.com': allowOnly({
    [DASHBOARD]: [VIEW, EDIT],
    [RESERVATIONS]: [VIEW, EDIT],
    [BOOKINGS]: [VIEW, EDIT],
    [SUBSCRIPTION]: [VIEW, EDIT],
  }),
};

// Returns the feature-permission map for a listed user, or null for everyone
// else (=> full access). Matches on username OR email, case-insensitively.
export const getUserAccess = ({ userId, email } = {}) => {
  const keys = [userId, email].filter(Boolean).map((value) =>
    String(value)
      .trim()
      .toLowerCase()
  );
  const hit = keys.find((key) => PERMISSIONS_BY_IDENTITY[key]);
  return hit ? PERMISSIONS_BY_IDENTITY[hit] : null;
};
